// Right panel — empty state, loading state, and full analysis results
import React from 'react';
import {
    Box, Typography, Paper, Chip, Grid, Avatar, Divider,
    CircularProgress, LinearProgress, alpha, useTheme,
} from '@mui/material';
import {
    Analytics as AnalyticsIcon,
    Assessment as AssessmentIcon,
    Tag as TagIcon,
    TextFields as TextFieldsIcon,
    Devices as DevicesIcon,
    TrendingUp as TrendingUpIcon,
    EmojiObjects as EmojiObjectsIcon,
    CheckCircle as CheckCircleIcon,
    AutoAwesome as AutoAwesomeIcon,
    SentimentSatisfiedAlt as SentimentPositiveIcon,
    SentimentDissatisfied as SentimentNegativeIcon,
    SentimentNeutral as SentimentNeutralIcon,
} from '@mui/icons-material';
import { ScoreRing, MetricTile } from './AnalyzePrimitives';
import { FEATURE_CHIPS } from './constants';

// ── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ isDark }) {
    return (
        <Box
            sx={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                textAlign: 'center', py: 10, px: 4,
                borderRadius: 3,
                background: isDark
                    ? 'linear-gradient(135deg, rgba(79,70,229,0.08), rgba(124,58,237,0.06))'
                    : 'linear-gradient(135deg, rgba(79,70,229,0.03), rgba(168,85,247,0.04))',
                border: `1.5px dashed ${alpha('#6366f1', 0.28)}`,
            }}
        >
            <Box sx={{
                width: 112, height: 112, borderRadius: '50%', mx: 'auto', mb: 4,
                background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(168,85,247,0.15))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 0 0 18px ${alpha('#6366f1', 0.06)}, 0 0 0 36px ${alpha('#6366f1', 0.03)}`,
            }}>
                <AnalyticsIcon sx={{ fontSize: 54, color: '#6366f1', opacity: 0.85 }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 1.5, letterSpacing: -0.3 }}>
                Unlock Content Insights
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 420, mx: 'auto', lineHeight: 1.8, mb: 4 }}>
                Paste your post on the left and click{' '}
                <Box component="span" sx={{ color: '#6366f1', fontWeight: 700 }}>Analyse Content</Box>{' '}
                to get AI-powered insights instantly.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                {FEATURE_CHIPS.map(f => (
                    <Chip key={f.label} label={f.label} size="small"
                        sx={{ bgcolor: alpha(f.color, 0.1), color: f.color, border: `1px solid ${alpha(f.color, 0.25)}`, fontWeight: 600, fontSize: '0.78rem' }}
                    />
                ))}
            </Box>
        </Box>
    );
}

// ── Loading state ────────────────────────────────────────────────────────────
function LoadingState() {
    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: 10 }}>
            <CircularProgress size={64} thickness={3} sx={{ color: '#6366f1', mb: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>Running AI Analysis…</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>Checking sentiment, readability, keywords and more</Typography>
            <LinearProgress sx={{ width: 280, borderRadius: 2, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#6366f1,#8b5cf6)' } }} />
        </Box>
    );
}

// ── Full results ─────────────────────────────────────────────────────────────
function AnalysisResults({ analysis, card }) {
    const sentimentColor =
        analysis.sentiment === 'positive' ? '#10b981' :
            analysis.sentiment === 'negative' ? '#ef4444' : '#f59e0b';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Score rings + stat tiles */}
            <Paper elevation={0} sx={card}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <AssessmentIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Analysis Results
                    </Typography>
                </Box>

                {/* Three rings */}
                <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 3, mb: 3, py: 1 }}>
                    {analysis.sentiment && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Avatar sx={{ bgcolor: alpha(sentimentColor, 0.12), color: sentimentColor, width: 60, height: 60, mx: 'auto', mb: 1 }}>
                                {analysis.sentiment === 'positive' ? <SentimentPositiveIcon sx={{ fontSize: 30 }} /> :
                                    analysis.sentiment === 'negative' ? <SentimentNegativeIcon sx={{ fontSize: 30 }} /> :
                                        <SentimentNeutralIcon sx={{ fontSize: 30 }} />}
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: sentimentColor, lineHeight: 1 }}>
                                {analysis.sentiment.charAt(0).toUpperCase() + analysis.sentiment.slice(1)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Sentiment</Typography>
                            {analysis.sentiment_score != null && (
                                <Typography variant="caption" sx={{ color: sentimentColor, fontWeight: 600 }}>
                                    {Math.round(analysis.sentiment_score * 100)}% confidence
                                </Typography>
                            )}
                        </Box>
                    )}
                    {analysis.engagement_score != null && (
                        <ScoreRing value={Math.round(analysis.engagement_score * 100)} color="#6366f1" size={90} label="Engagement" sub="Predicted potential" />
                    )}
                    {analysis.readability_score != null && (
                        <ScoreRing value={analysis.readability_score} max={10} color="#8b5cf6" size={90} label="Readability" sub="Flesch score" />
                    )}
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                {/* Stat tiles */}
                <Grid container spacing={2}>
                    {analysis.word_count != null && (
                        <Grid xs={6} sm={4}>
                            <MetricTile icon={<TextFieldsIcon sx={{ fontSize: 18 }} />} label="Words" value={analysis.word_count} sub={`${analysis.char_count ?? '—'} characters`} color="#06b6d4" />
                        </Grid>
                    )}
                    {analysis.best_platform && (
                        <Grid xs={6} sm={4}>
                            <MetricTile icon={<DevicesIcon sx={{ fontSize: 18 }} />} label="Best Platform" value={analysis.best_platform} sub="Recommended" color="#f59e0b" />
                        </Grid>
                    )}
                    {analysis.engagement_score != null && (
                        <Grid xs={6} sm={4}>
                            <MetricTile icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} label="Engagement" value={`${Math.round(analysis.engagement_score * 100)}%`} sub="Predicted" color="#10b981" />
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* Keywords + Entities */}
            {(analysis.keywords?.length > 0 || analysis.entities?.length > 0) && (
                <Grid container spacing={3}>
                    {analysis.keywords?.length > 0 && (
                        <Grid xs={12} sm={analysis.entities?.length > 0 ? 6 : 12}>
                            <Paper elevation={0} sx={card}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <TagIcon sx={{ color: '#6366f1', fontSize: 18 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>Keywords</Typography>
                                    <Chip label={analysis.keywords.length} size="small"
                                        sx={{ ml: 'auto', bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {analysis.keywords.map((kw, i) => (
                                        <Chip key={i} label={kw} size="small"
                                            sx={{ bgcolor: alpha('#6366f1', 0.08), color: '#6366f1', border: `1px solid ${alpha('#6366f1', 0.2)}`, fontWeight: 500, fontSize: '0.78rem' }}
                                        />
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>
                    )}
                    {analysis.entities?.length > 0 && (
                        <Grid xs={12} sm={analysis.keywords?.length > 0 ? 6 : 12}>
                            <Paper elevation={0} sx={card}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <EmojiObjectsIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>Entities</Typography>
                                    <Chip label={analysis.entities.length} size="small"
                                        sx={{ ml: 'auto', bgcolor: alpha('#f59e0b', 0.1), color: '#d97706', fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {analysis.entities.map(([ent, type], i) => (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderRadius: 1.5, bgcolor: alpha('#f59e0b', 0.05) }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.82rem' }}>{ent}</Typography>
                                            <Chip label={type} size="small"
                                                sx={{ bgcolor: alpha('#f59e0b', 0.12), color: '#d97706', fontWeight: 600, height: 18, fontSize: '0.65rem' }} />
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* AI Suggestions */}
            {analysis.suggestions?.length > 0 && (
                <Paper elevation={0} sx={card}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                        <AutoAwesomeIcon sx={{ color: '#ec4899', fontSize: 18 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>AI Suggestions</Typography>
                    </Box>
                    <Grid container spacing={1.5}>
                        {analysis.suggestions.map((s, i) => (
                            <Grid xs={12} sm={6} key={i}>
                                <Box sx={{
                                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                                    p: 2, borderRadius: 2.5,
                                    background: alpha('#10b981', 0.05),
                                    border: `1px solid ${alpha('#10b981', 0.15)}`,
                                }}>
                                    <CheckCircleIcon sx={{ color: '#10b981', fontSize: 15, mt: 0.3, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6, fontSize: '0.83rem' }}>{s}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            )}
        </Box>
    );
}

// ── ResultsPanel — orchestrates right column ─────────────────────────────────
export default function ResultsPanel({ analysis, loading, card }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const hasResults = !!(analysis && Object.keys(analysis).length > 0 && !analysis.error);

    if (loading) return <LoadingState />;
    if (!hasResults) return <EmptyState isDark={isDark} />;
    return <AnalysisResults analysis={analysis} card={card} />;
}
